"use client";

import { useSyncExternalStore } from "react";
import Image from "next/image";
import Link from "next/link";
import { useFeatureFlagVariantKey, usePostHog } from "posthog-js/react";
import {
  MdTrackChanges,
  MdMap,
  MdHowToReg,
  MdStar,
  MdFace,
  MdFace3,
  MdFace6,
} from "react-icons/md";
import { useAuthStore } from "@/store/useAuthStore";
import AppInstallBanner from "@/components/layout/AppInstallBanner";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.xephyrlabs.lattuai";

const HomeContent = () => {
  const posthog = usePostHog();
  const { user } = useAuthStore();
  const journeyHref = user
    ? "/journey-planner"
    : "/login/?redirect=/journey-planner";
  const heroVariant = useFeatureFlagVariantKey("home-page-hero-title");
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );

  return (
    <main className="overflow-x-clip">
      <AppInstallBanner />
      {/* Hero Section */}
      <section className="overflow-hidden pt-12 md:pt-20 pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center text-indigo-500 text-xs font-semibold mb-6">
              Built for Bangladeshis, by a Bangladeshi team that has been
              through this journey
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-[#000000] dark:text-[#ffffff] leading-[1.05] mb-6 tracking-tight">
              {mounted && heroVariant === "test" ? (
                <>
                  Your honest guide{" "}
                  <span className="text-indigo-500">to going abroad.</span>
                </>
              ) : (
                <>
                  Go abroad with a plan,{" "}
                  <span className="text-indigo-500">not a gamble</span>
                </>
              )}
            </h1>
            <p className="text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed mb-4 max-w-xl">
              Get a step-by-step plan built around you based on your preferences
              and goals. Compare countries and programs, know the real costs,
              requirements, and options before you apply.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                className="inline-block bg-indigo-500 text-white px-8 py-3.5 rounded-lg font-semibold text-base hover:brightness-110 active:translate-y-px transition-[filter,transform] duration-200"
                href={journeyHref}
                onClick={() =>
                  posthog?.capture("hero_cta_clicked", {
                    variant: heroVariant ?? "control",
                    is_logged_in: !!user,
                  })
                }
              >
                Create Your Journey
              </Link>
              <a
                className="inline-block border border-indigo-500 text-indigo-500 px-8 py-3.5 rounded-lg font-semibold text-base hover:bg-indigo-500/10 active:translate-y-px transition-[background-color,transform] duration-200"
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  posthog?.capture("app_download_click", {
                    app_platform: "android",
                    placement: "homepage_hero",
                    source_page: "homepage",
                    cta_text: "get_android_app",
                  })
                }
              >
                Get Android App
              </a>
            </div>
            <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
              Trusted by 1000+ students and families
            </p>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="rounded-xl overflow-hidden shadow-2xl bg-black border border-white dark:border-white/5 aspect-video">
              <video
                className="w-full h-full object-cover"
                controls
                loop
                preload="metadata"
                src="https://d27uuzzuinwnwc.cloudfront.net/Lattu_AI%20short_demo_with_caption.mp4"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="py-10 sm:py-12 bg-[#eceff1] dark:bg-[#1a1a1a] border-y border-zinc-200 dark:border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 sm:divide-x sm:divide-zinc-300 sm:dark:divide-white/10">
            <div className="px-4 py-4 text-center">
              <div className="text-3xl font-black text-indigo-500">18</div>
              <div className="mt-1 text-xs font-medium text-zinc-500 uppercase tracking-[0.15em]">
                Countries
              </div>
            </div>
            <div className="px-4 py-4 text-center">
              <div className="text-3xl font-black text-indigo-500">3,000+</div>
              <div className="mt-1 text-xs font-medium text-zinc-500 uppercase tracking-[0.15em]">
                Universities
              </div>
            </div>
            <div className="px-4 py-4 text-center">
              <div className="text-3xl font-black text-indigo-500">800+</div>
              <div className="mt-1 text-xs font-medium text-zinc-500 uppercase tracking-[0.15em]">
                Programs
              </div>
            </div>
            <div className="px-4 py-4 text-center">
              <div className="text-3xl font-black text-indigo-500">100+</div>
              <div className="mt-1 text-xs font-medium text-zinc-500 uppercase tracking-[0.15em]">
                Scholarships
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lattu Launchpad Grant 2026 */}
      {/* <section className="py-24 bg-[#eef0ff] dark:bg-[#0a0d1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_350px] gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-black text-black dark:text-white mb-4 leading-tight">
                Lattu Launchpad Grant 2026
              </h2>
              <p className="text-lg font-semibold text-indigo-500 mb-6">
                Airfare support for Bangladeshi students preparing to study
                abroad.
              </p>
              <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed mb-8">
                Every year, we award Bangladeshi students a grant toward their
                study abroad journey. You&apos;ve done a lot of work to get
                here. Lattu Launchpad Grant exists to support students who have
                already taken a major step. This year, we&apos;ll be awarding
                two students for airfare support.
              </p>
              <div className="flex flex-wrap items-center gap-3 mb-8 p-4 rounded-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-transparent w-fit">
                <span className="text-xs text-zinc-500 whitespace-nowrap">
                  Book flights via
                </span>
                <Image src="/go_zayaan_logo.png" alt="GoZayaan" width={108} height={34} className="object-contain dark:hidden" />
                <Image src="/White.png" alt="GoZayaan" width={108} height={34} className="object-contain hidden dark:block" />
                <div className="w-px h-5 bg-zinc-200 dark:bg-white/10 hidden sm:block"></div>
                <span className="text-xs text-zinc-500">
                  Official Booking Partner of the Lattu Launchpad Grant
                </span>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  href="/launchpad-grant/apply/"
                  className="inline-block bg-indigo-500 text-white px-8 py-3.5 rounded-lg font-semibold text-base hover:brightness-110 active:translate-y-px transition-[filter,transform] duration-200"
                >
                  Apply Now
                </Link>
                <Link
                  href="/launchpad-grant/"
                  className="inline-block border border-indigo-500 text-indigo-500 px-8 py-3.5 rounded-lg font-semibold text-base hover:bg-indigo-500/10 active:translate-y-px transition-[background-color,transform] duration-200"
                >
                  Learn More
                </Link>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-5 p-6 rounded-xl border border-indigo-100 dark:border-indigo-500/20 bg-white dark:bg-black shadow-sm">
                <div className="w-12 h-12 rounded-full bg-indigo-500/10 flex items-center justify-center shrink-0">
                  <LuUsers size={22} className="text-indigo-500" />
                </div>
                <div>
                  <div className="text-3xl font-black text-indigo-500 leading-none mb-1">
                    2
                  </div>
                  <div className="text-sm font-semibold text-black dark:text-white">
                    students will be awarded
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-5 p-6 rounded-xl border border-indigo-100 dark:border-indigo-500/20 bg-white dark:bg-black shadow-sm">
                <div className="w-12 h-12 rounded-full bg-indigo-500/10 flex items-center justify-center shrink-0">
                  <LuBanknote size={22} className="text-indigo-500" />
                </div>
                <div>
                  <div className="text-3xl font-black text-indigo-500 leading-none mb-1">
                    1,50,000 BDT
                  </div>
                  <div className="text-sm font-semibold text-black dark:text-white">
                    maximum per student
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-5 p-6 rounded-xl border border-indigo-100 dark:border-indigo-500/20 bg-white dark:bg-black shadow-sm">
                <div className="w-12 h-12 rounded-full bg-indigo-500/10 flex items-center justify-center shrink-0">
                  <LuCalendarDays size={22} className="text-indigo-500" />
                </div>
                <div>
                  <div className="text-3xl font-black text-indigo-500 leading-none mb-1">
                    July 10
                  </div>
                  <div className="text-sm font-semibold text-black dark:text-white">
                    Applications close
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* How It Works Section */}
      <section className="py-20 sm:py-28 bg-transparent relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12 sm:mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-black dark:text-white mb-4">
              How It Works
            </h2>
            <p className="text-zinc-500 dark:text-zinc-400 text-lg">
              Three simple steps to your dream education
            </p>
          </div>
          <div className="relative">
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-zinc-200 dark:bg-indigo-500/20 -translate-y-1/2 z-0"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
              {/* Step 1 */}
              <div className="flex flex-col items-center text-center group">
                <div className="w-20 h-20 rounded-full bg-white dark:bg-indigo-500/10 text-indigo-500 flex items-center justify-center mb-8 border-4 border-indigo-500 group-hover:bg-indigo-500 group-hover:text-white transition-colors duration-200 relative">
                  <MdTrackChanges size={36} />
                  <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-[#f0b100] text-white flex items-center justify-center font-bold text-sm">
                    1
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-3 dark:text-white">
                  Tell us about your goals
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed max-w-xs">
                  Tell us your goals, budget, and preferences through our
                  intuitive interface.
                </p>
              </div>
              {/* Step 2 */}
              <div className="flex flex-col items-center text-center group">
                <div className="w-20 h-20 rounded-full bg-white dark:bg-indigo-500/10 text-indigo-500 flex items-center justify-center mb-8 border-4 border-indigo-500 group-hover:bg-indigo-500 group-hover:text-white transition-colors duration-200 relative">
                  <MdMap size={36} />
                  <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-[#f0b100] text-white flex items-center justify-center font-bold text-sm">
                    2
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-3 dark:text-white">
                  Get your personalized plan
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed max-w-xs">
                  See which countries and programs make sense for your unique
                  profile.
                </p>
              </div>
              {/* Step 3 */}
              <div className="flex flex-col items-center text-center group">
                <div className="w-20 h-20 rounded-full bg-white dark:bg-indigo-500/10 text-indigo-500 flex items-center justify-center mb-8 border-4 border-indigo-500 group-hover:bg-indigo-500 group-hover:text-white transition-colors duration-200 relative">
                  <MdHowToReg size={36} />
                  <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-[#f0b100] text-white flex items-center justify-center font-bold text-sm">
                    3
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-3 dark:text-white">
                  Take the next step
                </h3>
                <p className="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed max-w-xs">
                  Move with clarity on costs, risks, and a solid long term plan
                  for your future.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-14 text-center">
            <Link
              className="inline-block bg-indigo-500 text-white px-8 py-3.5 rounded-lg font-semibold text-base hover:brightness-110 active:translate-y-px transition-[filter,transform] duration-200"
              href={journeyHref}
            >
              Create Your Journey
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 sm:py-28 bg-[#eceff1] dark:bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-black dark:text-white mb-4">
              Why students love using Lattu AI
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-white dark:bg-black p-6 sm:p-8 rounded-xl border border-zinc-200 dark:border-indigo-500/20 shadow-sm">
              <div className="flex gap-1 text-[#f0b100] mb-4">
                <MdStar size={18} />
                <MdStar size={18} />
                <MdStar size={18} />
                <MdStar size={18} />
                <MdStar size={18} />
              </div>
              <p className="text-[#000000] dark:text-zinc-300 leading-relaxed mb-6 text-sm">
                &quot;Lattu AI completely changed how I looked at study abroad.
                I found scholarships I didn&apos;t even know existed for
                Bangladeshi students.&quot;
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#3b82f6]/20 border border-[#3b82f6]/10 flex items-center justify-center overflow-hidden">
                  <MdFace className="text-3xl text-[#3b82f6] w-full h-full flex items-center justify-center" />
                </div>
                <div>
                  <div className="font-semibold text-sm dark:text-white">
                    Ahmed Z.
                  </div>
                  <div className="text-xs text-zinc-500">
                    Master&apos;s Student, Canada
                  </div>
                </div>
              </div>
            </div>
            {/* Testimonial 2 */}
            <div className="bg-white dark:bg-black p-6 sm:p-8 rounded-xl border border-zinc-200 dark:border-indigo-500/20 shadow-sm">
              <div className="flex gap-1 text-[#f0b100] mb-4">
                <MdStar size={18} />
                <MdStar size={18} />
                <MdStar size={18} />
                <MdStar size={18} />
                <MdStar size={18} />
              </div>
              <p className="text-[#000000] dark:text-zinc-300 leading-relaxed mb-6 text-sm">
                &quot;The cost breakdown was eye-opening. I could finally
                compare countries accurately without calling dozens of
                agents.&quot;
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#ec4899]/20 border border-[#ec4899]/10 flex items-center justify-center overflow-hidden">
                  <MdFace3 className="text-3xl text-[#ec4899] w-full h-full flex items-center justify-center" />
                </div>
                <div>
                  <div className="font-semibold text-sm dark:text-white">
                    Farzana R.
                  </div>
                  <div className="text-xs text-zinc-500">
                    Undergrad Applicant, Germany
                  </div>
                </div>
              </div>
            </div>
            {/* Testimonial 3 */}
            <div className="bg-white dark:bg-black p-6 sm:p-8 rounded-xl border border-zinc-200 dark:border-indigo-500/20 shadow-sm">
              <div className="flex gap-1 text-[#f0b100] mb-4">
                <MdStar size={18} />
                <MdStar size={18} />
                <MdStar size={18} />
                <MdStar size={18} />
                <MdStar size={18} />
              </div>
              <p className="text-[#000000] dark:text-zinc-300 leading-relaxed mb-6 text-sm">
                &quot;The step-by-step plan made the daunting visa process feel
                like a simple checklist. Truly built for our context.&quot;
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#11b981]/20 border border-[#11b981]/10 flex items-center justify-center overflow-hidden">
                  <MdFace6 className="text-3xl text-[#11b981] w-full h-full flex items-center justify-center" />
                </div>
                <div>
                  <div className="font-semibold text-sm dark:text-white">
                    Imtiaz H.
                  </div>
                  <div className="text-xs text-zinc-500">
                    PhD Scholar, Australia
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Banner */}
      {/* Lattu AI is now on Android */}
      <section className="py-20 sm:py-28 bg-transparent transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-16 overflow-hidden">
            <div className="lg:w-1/2">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4 dark:text-white">
                Lattu AI is now on Android
              </h2>
              <p className="text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed mb-8">
                Plan your study abroad journey from your phone.
              </p>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <a
                  className="flex items-center gap-3 px-5 py-3 bg-black dark:bg-white/10 border border-white/20 rounded-xl hover:-translate-y-0.5 transition-transform duration-200"
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    posthog?.capture("app_download_click", {
                      app_platform: "android",
                      placement: "app_section",
                      source_page: "homepage",
                      cta_text: "get_it_on_google_play",
                    })
                  }
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="w-6 h-6 fill-white shrink-0"
                  >
                    <path d="M3.18 23.76c.3.17.64.24.99.2l12.6-7.28-2.7-2.7zM.29 1.03C.1 1.35 0 1.73 0 2.15v19.7c0 .42.1.8.29 1.12l.06.06 11.04-11.04v-.26L.35.97zM20.38 10.4l-2.92-1.69-3.03 3.03 3.03 3.03 2.94-1.7c.84-.49.84-1.28-.02-1.67zM4.17.24l12.6 7.28-2.7 2.7z" />
                  </svg>
                  <div className="text-left">
                    <p className="text-white/60 text-[10px] leading-none">
                      Get it on
                    </p>
                    <p className="text-white font-semibold text-sm leading-tight">
                      Google Play
                    </p>
                  </div>
                </a>
                <Link
                  href="/app"
                  className="text-indigo-500 font-semibold text-sm hover:underline"
                  onClick={() =>
                    posthog?.capture("app_page_click", {
                      source_page: "homepage",
                    })
                  }
                >
                  See everything the app can do →
                </Link>
              </div>
              <p className="text-xs text-zinc-500">iOS version coming soon.</p>
            </div>
            <div className="lg:w-1/2 relative flex justify-center">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full bg-indigo-500/30 blur-[80px] pointer-events-none" />
              <div className="absolute top-1/4 left-1/3 w-40 h-40 rounded-full bg-violet-500/20 blur-[60px] pointer-events-none" />
              <Image
                src="/images/Lattu-mobile.png"
                alt="Lattu mobile app"
                width={320}
                height={640}
                className="relative z-10 drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 sm:py-28 bg-indigo-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6 text-white">
            Start your journey with clarity
          </h2>
          <p className="text-lg mb-10 max-w-2xl mx-auto font-medium text-white">
            You don&apos;t need to figure everything out alone
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              className="inline-block px-8 py-3.5 rounded-lg font-semibold text-base hover:brightness-110 active:translate-y-px transition-[filter,transform] duration-200 bg-white text-indigo-500"
              href={journeyHref}
            >
              Plan your journey with Lattu AI
            </Link>
            <a
              className="inline-block px-8 py-3.5 rounded-lg font-semibold text-base border border-white text-white hover:bg-white/10 active:translate-y-px transition-[background-color,transform] duration-200"
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                posthog?.capture("app_download_click", {
                  app_platform: "android",
                  placement: "final_cta",
                  source_page: "homepage",
                  cta_text: "get_android_app",
                })
              }
            >
              Get Android App
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomeContent;
